import { lazy, Suspense, useState } from "react";
import { ASSETS } from "@/lib/assets";
import { useSite } from "@/context/SiteContext";

const API = process.env.REACT_APP_BACKEND_URL;
const LABEL_SIZE = { xs: "text-[8px] !tracking-normal", sm: "text-[11px]", md: "text-xs", lg: "text-sm" };
const ReplaceButton = lazy(() => import("@/components/site/SlotEditor").then((m) => ({ default: m.ReplaceButton })));

export function useSlot(asset) {
  const { images } = useSite();
  const a = ASSETS[asset];
  const entry = asset === "TREE" ? null : images?.[asset];
  return { a, up: entry?.url ? entry : null, alt: entry?.alt || a.alt };
}

export const naturalRatio = (up, min = 0.66, max = 1.8) => Math.min(max, Math.max(min, up.width / up.height));

export function SlotImg({ up, alt, fit = "natural", eager = false, sizes = "100vw", fade = false, className = "", testId, stable }) {
  const [loaded, setLoaded] = useState(false);
  const src = stable ? `${API}/api/site-images/current/${stable}/small` : `${API}${up.small_url}`;
  return (
    <img src={src} srcSet={stable ? undefined : `${API}${up.small_url} ${up.small_width}w, ${API}${up.url} ${up.width}w`} sizes={stable ? undefined : sizes}
      width={up.width} height={up.height} alt={alt} loading={eager ? "eager" : "lazy"} decoding="async" fetchPriority={eager ? "high" : undefined}
      onLoad={() => setLoaded(true)} style={fit === "contain" ? undefined : { objectPosition: "50% 30%" }}
      className={`${fit === "contain" ? "object-contain" : "object-cover"} ${fade ? `of-img-fade ${loaded ? "is-loaded" : ""}` : ""} ${className}`}
      data-testid={testId} data-slot-image={up.url} />
  );
}

export function ReplaceSlot({ asset, className = "" }) {
  const { isAdmin } = useSite();
  if (!isAdmin || asset === "TREE") return null;
  return <Suspense fallback={null}><ReplaceButton asset={asset} className={className} /></Suspense>;
}

const Placeholder = ({ a, size, decorative, labelAlt, alt, testId }) => (
  <div className="of-placeholder flex h-full w-full flex-col items-center justify-center gap-1 overflow-hidden rounded-[inherit] text-center"
    role={decorative ? undefined : "img"} aria-label={decorative ? undefined : labelAlt ? alt : `${alt} (image coming soon)`}
    aria-hidden={decorative || undefined} data-testid={testId} data-placeholder={a.label}>
    <span className={`font-bold tracking-wide text-[#1B3530] ${LABEL_SIZE[size]} px-1 leading-tight [overflow-wrap:anywhere]`}>
      PLACEHOLDER-<wbr />{a.label.replace(/^PLACEHOLDER-/, "")}
    </span>
    {a.note && <span className={`text-[#4A5160] ${LABEL_SIZE[size]}`}>{a.note}</span>}
  </div>
);

export const AssetImage = ({ asset, className = "", size = "md", decorative = false, eager = false, labelAlt = false, sizes, noEdit = false, replaceClass, testId }) => {
  const { a, up, alt: baseAlt } = useSlot(asset);
  const alt = labelAlt && !up ? `${a.label}: ${baseAlt}` : baseAlt;
  const pos = className.includes("absolute") ? "" : "relative";
  const style = up && a.fit === "natural" ? { aspectRatio: naturalRatio(up) } : undefined;
  return (
    <div className={`${pos} ${className}`} style={style} data-testid={testId && `${testId}-slot`}>
      {up
        ? <SlotImg up={up} alt={decorative ? "" : alt} fit={a.fit} eager={eager} sizes={sizes} stable={asset === "OLIVIA_HERO" ? asset : undefined} className="h-full w-full rounded-[inherit]" testId={testId} />
        : <Placeholder a={a} size={size} decorative={decorative} labelAlt={labelAlt} alt={alt} testId={testId} />}
      {!noEdit && <ReplaceSlot asset={asset} className={replaceClass ?? (size === "xs" ? "!bottom-auto !left-auto right-0 top-full mt-1 w-[168px] !max-w-none" : "")} />}
    </div>
  );
};

export const DecorAccent = ({ asset, className = "" }) => (
  <AssetImage asset={asset} decorative size="xs" sizes="80px" className={`h-20 w-20 shrink-0 rounded-2xl ${className}`}
    testId={`decor-${asset.toLowerCase()}`} />
);
